import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuthContext } from '../hooks/AuthContext';
import useAdminAutoRefresh from './useAdminAutoRefresh';
import './AdminUsers.css';

const AUTO_REFRESH_INTERVAL_MS = 5000;

const NOTE_CATEGORIES = ['Support', 'Credits', 'Payment', 'Testing', 'Account', 'Other'];

function formatDateTime(value) {
  if (!value) {
    return 'Unknown';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);
}

function formatDuration(seconds) {
  if (!seconds) {
    return 'Unknown';
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

function formatEntitlementMeta(item) {
  const parts = [];

  if (item.access_type === 'included') {
    parts.push('Included by default');
  }

  if (item.granted_at) {
    parts.push(`Granted ${formatDateTime(item.granted_at)}`);
  }

  if (item.expires_at) {
    parts.push(`Expires ${formatDateTime(item.expires_at)}`);
  }

  return parts.join(' • ');
}

const AdminUserDetailPage = () => {
  const { userId } = useParams();
  const { authenticatedUser } = useAuthContext();
  const [state, setState] = useState({
    loading: true,
    refreshing: false,
    error: '',
    data: null,
    lastUpdatedAt: null,
  });
  const [noteForm, setNoteForm] = useState({
    category: 'Support',
    note_text: '',
  });
  const [creditForm, setCreditForm] = useState({
    adjustment_type: 'add',
    credit_amount: '',
    reason: '',
    note_text: '',
  });
  const [statusForm, setStatusForm] = useState({
    reason: '',
    note_text: '',
  });
  const [internalForm, setInternalForm] = useState({
    reason: '',
    note_text: '',
  });
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const { refreshNonce, triggerRefresh } = useAdminAutoRefresh({
    enabled: Boolean(authenticatedUser?.token && userId),
    intervalMs: AUTO_REFRESH_INTERVAL_MS,
  });

  const loadUserDetail = useCallback(async (signal, { background = false } = {}) => {
    if (!authenticatedUser?.token || !userId) {
      return;
    }

    setState((current) => ({
      ...current,
      loading: background ? current.loading : true,
      refreshing: background,
      error: '',
    }));

    try {
      const response = await fetch(`/.netlify/functions/admin-get-user-detail?user_id=${encodeURIComponent(userId)}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authenticatedUser.token}`,
        },
        signal,
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || 'Unable to load user details.');
      }

      setState({
        loading: false,
        refreshing: false,
        error: '',
        data,
        lastUpdatedAt: new Date().toISOString(),
      });
    } catch (error) {
      if (signal?.aborted) {
        return;
      }

      setState({
        loading: false,
        refreshing: false,
        error: error.message || 'Unable to load user details.',
        data: null,
        lastUpdatedAt: null,
      });
    }
  }, [authenticatedUser, userId]);

  useEffect(() => {
    const controller = new AbortController();
    loadUserDetail(controller.signal, { background: refreshNonce > 0 });
    return () => controller.abort();
  }, [loadUserDetail, refreshNonce]);

  const postAction = async (url, payload, successMessage) => {
    setSaving(true);
    setMessage('');

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authenticatedUser.token}`,
        },
        body: JSON.stringify({
          user_id: userId,
          ...payload,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || 'Action failed.');
      }

      setMessage(successMessage);
      await loadUserDetail(undefined, { background: true });
      return data;
    } catch (error) {
      setMessage(error.message || 'Action failed.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  if (state.loading) {
    return (
      <section className="cq-admin-page">
        <div className="cq-admin-empty-state">
          <p className="cq-admin-empty-state__title">Loading user details...</p>
        </div>
      </section>
    );
  }

  if (state.error || !state.data?.user) {
    return (
      <section className="cq-admin-page">
        <div className="cq-admin-empty-state">
          <p className="cq-admin-empty-state__title">User not found.</p>
          <p className="cq-admin-empty-state__copy">{state.error || 'Unable to load user details.'}</p>
          <Link className="cq-admin-status-link" to="/admin/users">Back to Users</Link>
        </div>
      </section>
    );
  }

  const {
    user,
    wallet,
    activity,
    recent_sessions: recentSessions,
    payments,
    admin_notes: adminNotes,
    preferences,
    owned_content: ownedContent = { game_packs: [], quest_packs: [] },
  } = state.data;

  return (
    <section className="cq-admin-page">
      <header className="cq-admin-page__header">
        <div>
          <p className="cq-admin-page__eyebrow">User Management</p>
          <h2 className="cq-admin-page__title">{user.email || 'Unknown User'}</h2>
          <p className="cq-admin-page__copy">{user.user_id}</p>
        </div>
        <div className="cq-admin-page__actions">
          <div className="cq-admin-page__actions-row">
            <Link className="cq-admin-status-link" to="/admin/users">Back to Users</Link>
            <button
              type="button"
              className="cq-admin-action-button cq-admin-action-button--secondary"
              disabled={state.loading || state.refreshing || saving}
              onClick={triggerRefresh}
            >
              Refresh
            </button>
          </div>
          <p className="cq-admin-page__meta">
            {state.refreshing ? 'Refreshing...' : `Last updated ${formatDateTime(state.lastUpdatedAt)}`}
          </p>
        </div>
      </header>

      {message ? <div className="cq-admin-empty-inline">{message}</div> : null}

      <div className="cq-admin-users-grid">
        <section className="cq-admin-panel">
          <h3 className="cq-admin-section-title">Account Summary</h3>
          <dl className="cq-admin-definition-list">
            <div><dt>Email</dt><dd>{user.email || 'Unknown'}</dd></div>
            <div><dt>User ID</dt><dd className="cq-admin-cell-id">{user.user_id}</dd></div>
            <div><dt>Created At</dt><dd>{formatDateTime(user.created_at)}</dd></div>
            <div><dt>Last Login</dt><dd>{formatDateTime(user.last_login_at)}</dd></div>
            <div><dt>Last Active</dt><dd>{formatDateTime(user.last_active_at)}</dd></div>
            <div><dt>Account Status</dt><dd>{user.status}</dd></div>
            <div><dt>Internal/Test</dt><dd>{user.is_internal_test ? 'Yes' : 'No'}</dd></div>
            <div><dt>Admin User</dt><dd>{user.is_admin ? 'Yes' : 'No'}</dd></div>
          </dl>
        </section>

        <section className="cq-admin-panel">
          <h3 className="cq-admin-section-title">Credit / Wallet Summary</h3>
          <dl className="cq-admin-definition-list">
            <div><dt>Current Balance</dt><dd>{wallet.credit_balance}</dd></div>
            <div><dt>Lifetime Purchased</dt><dd>{wallet.lifetime_credits_purchased}</dd></div>
            <div><dt>Lifetime Consumed</dt><dd>{wallet.lifetime_credits_consumed}</dd></div>
            <div><dt>Manual Credits Granted</dt><dd>{wallet.manual_credits_granted}</dd></div>
            <div><dt>Manual Credits Removed</dt><dd>{wallet.manual_credits_removed}</dd></div>
            <div><dt>Last Credit Purchase</dt><dd>{formatDateTime(wallet.last_credit_purchase_at)}</dd></div>
            <div><dt>Last Credit Usage</dt><dd>{formatDateTime(wallet.last_credit_usage_at)}</dd></div>
          </dl>
        </section>

        <section className="cq-admin-panel">
          <h3 className="cq-admin-section-title">Game Activity Summary</h3>
          <dl className="cq-admin-definition-list">
            <div><dt>Games Started</dt><dd>{activity.games_started}</dd></div>
            <div><dt>Games Completed</dt><dd>{activity.games_completed}</dd></div>
            <div><dt>Games Failed</dt><dd>{activity.games_failed}</dd></div>
            <div><dt>Last Game</dt><dd>{formatDateTime(activity.last_game_at)}</dd></div>
            <div><dt>Avg Session Duration</dt><dd>{formatDuration(activity.average_session_duration_seconds)}</dd></div>
            <div><dt>Avg Estimated Cost</dt><dd>{formatCurrency(activity.average_estimated_cost)}</dd></div>
          </dl>
        </section>

        <section className="cq-admin-panel">
          <h3 className="cq-admin-section-title">Owned Content</h3>

          <div className="cq-admin-owned-content">
            <div>
              <h4 className="cq-admin-owned-content__title">Game Packs</h4>
              {ownedContent.game_packs?.length ? (
                <ul className="cq-admin-owned-content__list">
                  {ownedContent.game_packs.map((item) => (
                    <li key={item.id} className="cq-admin-owned-content__item">
                      <div className="cq-admin-owned-content__name">{item.name}</div>
                      <div className="cq-admin-owned-content__meta">
                        {item.slug}
                        {formatEntitlementMeta(item) ? ` • ${formatEntitlementMeta(item)}` : ''}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="cq-admin-empty-inline">No owned game packs.</div>
              )}
            </div>

            <div>
              <h4 className="cq-admin-owned-content__title">Quest Packs</h4>
              {ownedContent.quest_packs?.length ? (
                <ul className="cq-admin-owned-content__list">
                  {ownedContent.quest_packs.map((item) => (
                    <li key={item.id} className="cq-admin-owned-content__item">
                      <div className="cq-admin-owned-content__name">{item.name}</div>
                      <div className="cq-admin-owned-content__meta">
                        {item.slug}
                        {formatEntitlementMeta(item) ? ` • ${formatEntitlementMeta(item)}` : ''}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="cq-admin-empty-inline">No owned quest packs.</div>
              )}
            </div>
          </div>
        </section>
      </div>

      <div className="cq-admin-users-grid cq-admin-users-grid--actions">
        <section className="cq-admin-panel">
          <h3 className="cq-admin-section-title">Adjust Credits</h3>
          <div className="cq-admin-form-grid">
            <label className="cq-admin-field">
              <span>Adjustment Type</span>
              <select className="cq-admin-input" value={creditForm.adjustment_type} onChange={(event) => setCreditForm((current) => ({ ...current, adjustment_type: event.target.value }))}>
                <option value="add">Add</option>
                <option value="remove">Remove</option>
              </select>
            </label>
            <label className="cq-admin-field">
              <span>Credit Amount</span>
              <input className="cq-admin-input" type="number" min="1" value={creditForm.credit_amount} onChange={(event) => setCreditForm((current) => ({ ...current, credit_amount: event.target.value }))} />
            </label>
          </div>
          <label className="cq-admin-field">
            <span>Reason</span>
            <input className="cq-admin-input" value={creditForm.reason} onChange={(event) => setCreditForm((current) => ({ ...current, reason: event.target.value }))} />
          </label>
          <label className="cq-admin-field">
            <span>Optional Note</span>
            <textarea className="cq-admin-input cq-admin-textarea" value={creditForm.note_text} onChange={(event) => setCreditForm((current) => ({ ...current, note_text: event.target.value }))} />
          </label>
          <button
            type="button"
            className="cq-admin-action-button"
            disabled={saving}
            onClick={async () => {
              const result = await postAction('/.netlify/functions/admin-adjust-user-credits', {
                adjustment_type: creditForm.adjustment_type,
                credit_amount: Number(creditForm.credit_amount),
                reason: creditForm.reason,
                note_text: creditForm.note_text,
              }, 'Credits updated successfully.');

              if (result?.success) {
                setCreditForm({
                  adjustment_type: 'add',
                  credit_amount: '',
                  reason: '',
                  note_text: '',
                });
              }
            }}
          >
            Adjust Credits
          </button>
        </section>

        <section className="cq-admin-panel">
          <h3 className="cq-admin-section-title">Account Actions</h3>
          <label className="cq-admin-field">
            <span>Status Reason</span>
            <input className="cq-admin-input" value={statusForm.reason} onChange={(event) => setStatusForm((current) => ({ ...current, reason: event.target.value }))} />
          </label>
          <label className="cq-admin-field">
            <span>Optional Note</span>
            <textarea className="cq-admin-input cq-admin-textarea" value={statusForm.note_text} onChange={(event) => setStatusForm((current) => ({ ...current, note_text: event.target.value }))} />
          </label>
          <div className="cq-admin-inline-actions">
            <button
              type="button"
              className="cq-admin-action-button"
              disabled={saving}
              onClick={() => postAction('/.netlify/functions/admin-update-user-status', {
                status: 'disabled',
                reason: statusForm.reason,
                note_text: statusForm.note_text,
              }, 'User disabled successfully.')}
            >
              Disable User
            </button>
            <button
              type="button"
              className="cq-admin-action-button cq-admin-action-button--secondary"
              disabled={saving}
              onClick={() => postAction('/.netlify/functions/admin-update-user-status', {
                status: 'active',
                reason: statusForm.reason,
                note_text: statusForm.note_text,
              }, 'User re-enabled successfully.')}
            >
              Re-enable User
            </button>
          </div>

          <label className="cq-admin-field">
            <span>Internal/Test Reason</span>
            <input className="cq-admin-input" value={internalForm.reason} onChange={(event) => setInternalForm((current) => ({ ...current, reason: event.target.value }))} />
          </label>
          <label className="cq-admin-field">
            <span>Optional Note</span>
            <textarea className="cq-admin-input cq-admin-textarea" value={internalForm.note_text} onChange={(event) => setInternalForm((current) => ({ ...current, note_text: event.target.value }))} />
          </label>
          <div className="cq-admin-inline-actions">
            <button
              type="button"
              className="cq-admin-action-button"
              disabled={saving}
              onClick={() => postAction('/.netlify/functions/admin-update-user-internal-flag', {
                is_internal_test: true,
                reason: internalForm.reason,
                note_text: internalForm.note_text,
              }, 'User marked as internal/test successfully.')}
            >
              Mark Internal/Test
            </button>
            <button
              type="button"
              className="cq-admin-action-button cq-admin-action-button--secondary"
              disabled={saving}
              onClick={() => postAction('/.netlify/functions/admin-update-user-internal-flag', {
                is_internal_test: false,
                reason: internalForm.reason,
                note_text: internalForm.note_text,
              }, 'Internal/test flag removed successfully.')}
            >
              Remove Internal/Test
            </button>
          </div>
        </section>
      </div>

      <section className="cq-admin-panel">
        <h3 className="cq-admin-section-title">Admin Notes</h3>
        <div className="cq-admin-form-grid">
          <label className="cq-admin-field">
            <span>Category</span>
            <select className="cq-admin-input" value={noteForm.category} onChange={(event) => setNoteForm((current) => ({ ...current, category: event.target.value }))}>
              {NOTE_CATEGORIES.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </label>
        </div>
        <label className="cq-admin-field">
          <span>Note</span>
          <textarea className="cq-admin-input cq-admin-textarea" value={noteForm.note_text} onChange={(event) => setNoteForm((current) => ({ ...current, note_text: event.target.value }))} />
        </label>
        <button
          type="button"
          className="cq-admin-action-button"
          disabled={saving}
          onClick={async () => {
            const result = await postAction('/.netlify/functions/admin-add-user-note', noteForm, 'Admin note added successfully.');
            if (result?.success) {
              setNoteForm({
                category: 'Support',
                note_text: '',
              });
            }
          }}
        >
          Save Note
        </button>

        {adminNotes.length ? (
          <ul className="cq-admin-note-list">
            {adminNotes.map((note) => (
              <li key={note.id} className="cq-admin-note">
                <div className="cq-admin-note__meta">
                  <span>{note.category}</span>
                  <span>{note.created_by_email || note.created_by}</span>
                  <span>{formatDateTime(note.created_at)}</span>
                </div>
                <p>{note.note_text}</p>
              </li>
            ))}
          </ul>
        ) : (
          <div className="cq-admin-empty-inline">No admin notes yet.</div>
        )}
      </section>

      <div className="cq-admin-users-grid">
        <section className="cq-admin-panel">
          <h3 className="cq-admin-section-title">Recent Sessions</h3>
          {recentSessions.length ? (
            <div className="cq-admin-table-wrap">
              <table className="cq-admin-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Game Pack</th>
                    <th>Host</th>
                    <th>Status</th>
                    <th>Duration</th>
                    <th>Credits Used</th>
                    <th>Estimated Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {recentSessions.map((session) => (
                    <tr key={session.id}>
                      <td>{formatDateTime(session.date)}</td>
                      <td>{session.game_pack_name || 'Unknown'}</td>
                      <td>{session.host_name || 'Unknown'}</td>
                      <td>{session.status}</td>
                      <td>{formatDuration(session.duration_seconds)}</td>
                      <td>{session.credits_used}</td>
                      <td>{formatCurrency(session.estimated_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="cq-admin-empty-inline">No recent sessions found.</div>
          )}
        </section>

        <section className="cq-admin-panel">
          <h3 className="cq-admin-section-title">Payment History</h3>
          {payments.length ? (
            <div className="cq-admin-table-wrap">
              <table className="cq-admin-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Product / Bundle</th>
                    <th>Amount</th>
                    <th>Credits Purchased</th>
                    <th>Stripe Status</th>
                    <th>Wallet Updated?</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id}>
                      <td>{formatDateTime(payment.date)}</td>
                      <td>{payment.product_name}</td>
                      <td>{formatCurrency((payment.amount_total_cents || 0) / 100)}</td>
                      <td>{payment.credits_purchased}</td>
                      <td>{payment.stripe_status}</td>
                      <td>{payment.wallet_updated ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="cq-admin-empty-inline">No payments found.</div>
          )}
        </section>
      </div>

      <section className="cq-admin-panel">
        <h3 className="cq-admin-section-title">Current Preferences</h3>
        {preferences ? (
          <pre className="cq-admin-pre">{JSON.stringify(preferences, null, 2)}</pre>
        ) : (
          <div className="cq-admin-empty-inline">No preferences found.</div>
        )}
      </section>
    </section>
  );
};

export default AdminUserDetailPage;
